// dependencies
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import useDeepCompareEffect from "use-deep-compare-effect";

// hooks
import useCachedDataFetcher from "./useCachedDataFetcher";

import { translateImmutableValues, translateUpdatesToStrings } from "./helperFunctions";

/**
 * This hook stores the state of filters as query parameters.
 * It takes in an array of configurations which formats the filters
 * into a query string based on configuration type.
 * It also takes the query string and parses it into usable formats.
 * @param {array} configuration array of objects with keys and values:
 *                 -param : name of parameter
 *                 -type : type of filter data
 */

const useQueryParams = (configuration) => {
	const [isPending, setIsPending] = useState(true);
	const [searchParams, setSearchParams] = useSearchParams();
	const [parsedQueryParams, setParsedQueryParams] = useState({});

	const { getFullValue } = useCachedDataFetcher();

	const queryString = searchParams.toString();

	const params = Object.fromEntries(searchParams);

	// takes filter state object and filters the data to be stored in
	// query param
	const setQueryState = (updates) => {
		const parsedUpdates = translateUpdatesToStrings(updates, configuration);
		const nextQueryParamsState = { ...params, ...parsedUpdates };

		// remove any keys that are null, undefined, empty string, or empty array
		const cleanedParsedUpdates = Object.keys(nextQueryParamsState).reduce((acc, key) => {
			const value = nextQueryParamsState[key];

			if (value !== null && value !== undefined && value !== "" && value.length !== 0) {
				acc[key] = value;
			}
			return acc;
		}, {});

		setSearchParams(cleanedParsedUpdates);
	};

	const parseConfiguration = async () => {
		setIsPending(true);

		const pendingParsedValues = configuration.map(async (config) => {
			// get the raw value from the query param string
			const rawValue = searchParams.getAll(config.param);
			if (!rawValue.length || (rawValue.length === 1 && rawValue[0] === "")) {
				return null;
			}

			const parsedRawValue = await getFullValue(rawValue, config.type);

			return {
				rawValue,
				name: config.param,
				type: config.type,
				value: parsedRawValue,
			};
		});

		// wait for all parsed data to resolve
		const parsedValues = await Promise.all(pendingParsedValues);

		// remove null values
		const filteredParsedValues = parsedValues.filter((d) => d !== null);

		// add a immutable abstraction layer to keep immutable data separate
		const immutableParsedValues = translateImmutableValues(filteredParsedValues);

		// return an object of results
		const parsedMap = immutableParsedValues.reduce((acc, current) => {
			acc[current.name] = current;
			return acc;
		}, {});

		setParsedQueryParams(parsedMap);
		setIsPending(false);
	};

	useDeepCompareEffect(() => {
		const init = async () => {
			parseConfiguration();
		};
		init();
	}, [configuration, queryString]);

	return {
		setQueryState,
		isLoading: isPending,
		queryState: parsedQueryParams,
	};
};

export default useQueryParams;
